import { SuggestedSeriesDismissDialog } from 'src/component/lets-get-moving/suggested-series-dismiss-dialog'
import { SuggestedSeriesExhausted } from 'src/component/lets-get-moving/suggested-series-exhausted'
import { SuggestedSeriesLoadingNextOverlay } from 'src/component/lets-get-moving/suggested-series-loading-next-overlay'
import { SuggestedSectionTitle } from 'src/component/lets-get-moving/suggested-section-title'
import { SuggestedSeriesSkeleton } from 'src/component/lets-get-moving/suggested-series-skeleton'
import { SuggestedSeriesCard } from 'src/component/ui/suggested-series-card'
import { useCountdownOverlay } from 'src/hook/use-countdown-overlay'
import { useDismissSuggestedSeries } from 'src/hook/use-dismiss-suggested-series'
import { usePreviewSeries } from 'src/hook/use-preview-series'
import { useSuggestedSeries } from 'src/hook/use-suggested-series'
import React, { useState } from 'react'

const SUGGESTED_DISMISS_MIN_SECONDS = 5

export const SuggestedSeries = (): React.ReactElement => {
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false)

  const {
    suggestionState,
    suggestedSeries,
    nextSeriesWorkout,
    completedClassesCount,
    totalClassesCount,
    isProgressReady,
    isLoading,
    isSuccess,
    isFetching,
  } = useSuggestedSeries()

  const dismissSuggestedSeries = useDismissSuggestedSeries()
  const { previewSeriesModal, startSeriesPreview } = usePreviewSeries(nextSeriesWorkout, 'suggested')
  const loadingNext = useCountdownOverlay(SUGGESTED_DISMISS_MIN_SECONDS, isFetching)

  const progressCount = completedClassesCount ?? 0
  /** Reserve progress row while counts load (SeriesCardProgress skeleton); hide when ready with zero classes. */
  const showProgress = !isProgressReady || (totalClassesCount ?? 0) > 0

  const handleConfirmDismiss = (): void => {
    if (!suggestedSeries?.id) {
      return
    }
    dismissSuggestedSeries.mutate(suggestedSeries.id, {
      onSuccess: () => loadingNext.start(),
      onSettled: () => setDismissDialogOpen(false),
    })
  }

  if (isLoading && !isSuccess) {
    return (
      <div data-testid="suggested-series-section">
        <div className="mb-8 lg:mb-10">
          <SuggestedSectionTitle>Series</SuggestedSectionTitle>
        </div>
        <div data-nextstep="suggested-series">
          <SuggestedSeriesSkeleton />
        </div>
      </div>
    )
  }

  if (!loadingNext.isVisible && (!isSuccess || suggestionState === undefined)) {
    return <></>
  }

  if (loadingNext.isVisible) {
    return (
      <div data-testid="suggested-series-section">
        <div className="mb-8 lg:mb-10">
          <SuggestedSectionTitle>Series</SuggestedSectionTitle>
        </div>
        <SuggestedSeriesLoadingNextOverlay secondsRemaining={loadingNext.secondsRemaining} />
      </div>
    )
  }

  if (suggestionState === 'exhausted') {
    return <SuggestedSeriesExhausted />
  }

  const hasSeriesCard = !!suggestedSeries && (suggestionState === 'active' || suggestionState === 'inactive')

  if (!hasSeriesCard) {
    return <></>
  }

  const isActiveSuggested = suggestionState === 'active'
  const cardTestId = isActiveSuggested ? 'suggested-series-active-card' : 'suggested-series-continue-card'

  return (
    <div data-testid="suggested-series-section">
      <div className="mb-8 lg:mb-10">
        <SuggestedSectionTitle>Series</SuggestedSectionTitle>
      </div>
      <div data-nextstep="suggested-series">
        <SuggestedSeriesCard
          series={suggestedSeries}
          onPreview={() => startSeriesPreview(suggestedSeries)}
          data-testid={cardTestId}
          isActiveSuggested={isActiveSuggested}
          onDismissClick={isActiveSuggested ? () => setDismissDialogOpen(true) : undefined}
          nextWorkoutTitle={nextSeriesWorkout?.workout.title ?? ''}
          completedCount={progressCount}
          totalCount={totalClassesCount}
          showProgress={showProgress}
          isProgressReady={isProgressReady}
          isDismissPending={dismissSuggestedSeries.isLoading}
        />
      </div>
      {isActiveSuggested && (
        <SuggestedSeriesDismissDialog
          isOpen={dismissDialogOpen}
          onClose={() => setDismissDialogOpen(false)}
          onDismiss={handleConfirmDismiss}
          isSubmitting={dismissSuggestedSeries.isLoading}
        />
      )}
      {previewSeriesModal}
    </div>
  )
}
